// Taşeron portalı — Uygunluk belgelerim (api-contract §13.6 + §8).
// `GET /supplier/contractor/documents` liste (tür, dosya, geçerlilik, **durum rozeti**
// Valid/Expiring/Expired/Missing) + belge yükle (§8 → §13.6 link) + indir (§8 presigned
// GET) + sil. Supplier **kendi** taşeronunun belgelerini yönetir (kimlik token/bağdan).
// Uygunluk özeti belgelerden türetilir → değişimde firma bilgisi (dashboard) tazelenir.

import {
  Breadcrumb,
  Button,
  ButtonSet,
  Card,
  CardContent,
  ConfirmDialog,
  type ContractorDocumentDto,
  DOCUMENT_ACCEPT_ATTR,
  EmptyState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  Tag,
  currentCulture,
  documentsApi,
  supplierContractorApi,
  useAuthStore,
  useDocumentTypes,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Hourglass, Trash2, Upload } from 'lucide-react';
import { type DragEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalysisInboxButton } from '../components/AnalysisInboxButton';
import { ContractorStateNotice } from '../components/ContractorStateNotice';
import { DropOverlay } from '../components/DropOverlay';
import { PendingAnalysesStrip } from '../components/PendingAnalysesStrip';
import { StrandedAnalysesStrip } from '../components/StrandedAnalysesStrip';
import { UploadModal } from '../components/UploadModal';
import { complianceColor } from '../components/status/tone';
import { formatDate } from '../lib/format';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';
import { useTableDensity } from '../lib/uiPrefs';
import { useReturnFocus } from '../lib/useReturnFocus';

/** Geçerlilik aralığı görüntü metni (DateOnly, §4); ikisi de yoksa tire. */
function validityRange(document: ContractorDocumentDto, culture: string): string {
  const from = document.validFrom ? formatDate(document.validFrom, culture) : null;
  const until = document.validUntil ? formatDate(document.validUntil, culture) : null;
  if (from && until) {
    return `${from} –\u00A0${until}`;
  }
  return until ?? from ?? '—';
}

export function DocumentsPage() {
  const { t } = useTranslation();
  // Tür etiketi §27 kaydından (`company`; kayıtta yoksa ham kod) — GL #7.
  const { label: typeLabel } = useDocumentTypes();
  const queryClient = useQueryClient();
  const culture = currentCulture();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);

  const [toDelete, setToDelete] = useState<ContractorDocumentDto | null>(null);
  // Onay diyaloğunun tetikleyicisi SATIR BAŞINA değişir — sabit bir `ref` yerine tıklama anında
  // yakalanır. Vazgeç/Esc yolunda satır hâlâ yerindedir, odak oraya döner (QA D12'nin aynı
  // kusuru bu yüzeyde). Başarılı silmede satır zaten kalkar; o yol `onOpenChange`'ten geçmez.
  const deleteTrigger = useReturnFocus();

  // Yükleme tetikleyicisi — gerçek `<button>` + gizli `<input type="file">` (klavye yolu,
  // HER ZAMAN mevcut) + sayfa gövdesine sürükle-bırak (yalnız işaretçi cihazlarda EKLENTİ).
  // İkisi de `pickedFiles`'a YENİ bir dizi referansı yazar — `UploadModal` her seçimi bir
  // ÖNCEKİ referansla karşılaştırıp yalnız DEĞİŞTİĞİNDE işler (bkz. o bileşenin `files` prop
  // yorumu); aynı referansı ikinci kez vermek onu bir daha İŞLEMEZ.
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Modal kapanınca odağın DÖNECEĞİ tetikleyici (AC31'in kabul akışına koyduğu kuralın aynısı,
  // yükleme yüzeyine uygulanmış — QA D2). Radix'in kendi geri-yükleme mekanizması burada
  // yetmiyor (bkz. `useReturnFocus`); üstüne bu düğme gizli `<input type="file">`i tıklıyor,
  // yani "önceki odak" o gizli girdidir ve oraya dönmek odağı KAYBETMEKLE aynı şey.
  const uploadTrigger = useReturnFocus();
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  function beginUpload(files: File[]) {
    if (files.length === 0) {
      return;
    }
    setPickedFiles(files);
    setUploadOpen(true);
  }

  // Sürükleme SAYACI, boolean DEĞİL — `dragleave` tablo satırları/kartlar gibi her ÇOCUK
  // elemana geçişte ateşlenir; bir sayaç olmadan kaplama (`DropOverlay`) TİTRER (tasarım
  // review §4, plan S19-D7). Kaplama `absolute` bu sayfanın `relative` kabı İÇİNDE — ASLA
  // `fixed` (başlığı/navı/paneli ÖRTMEZ).
  const dragCounterRef = useRef(0);
  const [dragActive, setDragActive] = useState(false);

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    if (!e.dataTransfer.types.includes('Files')) {
      return;
    }
    e.preventDefault();
    dragCounterRef.current += 1;
    setDragActive(true);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    if (!e.dataTransfer.types.includes('Files')) {
      return;
    }
    e.preventDefault(); // sürükleneni BIRAKILABİLİR say — tarayıcı varsayılanı (aç/indir) engellenir.
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    if (dragCounterRef.current === 0) {
      return;
    }
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setDragActive(false);
    }
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragActive(false);
    // FileList ANINDA diziye alınır — `dataTransfer` `input.value` gibi elle sıfırlanmaz ama
    // aynı disiplin (DocumentUploadModal.tsx:216-222): önce anlık görüntü, sonra kullan.
    beginUpload(Array.from(e.dataTransfer.files));
  }

  const query = useQuery({
    queryKey: supplierContractorKeys.documents(companyId),
    queryFn: () => supplierContractorApi.listSupplierContractorDocuments(),
  });

  const downloadMutation = useMutation({
    mutationFn: (documentUuid: string) => documentsApi.getDocument(documentUuid),
    onSuccess: (download) => {
      // Presigned GET — özgün dosya adıyla iner (content-disposition attachment).
      window.open(download.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentUuid: string) =>
      supplierContractorApi.deleteSupplierDocument(documentUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierContractorKeys.documents(companyId) });
      queryClient.invalidateQueries({ queryKey: supplierContractorKeys.contractor(companyId) });
      setToDelete(null);
    },
  });

  const rows = query.data ?? [];
  const [density, setDensity] = useTableDensity();
  // Bir analizin "hâlâ aksıyor mu" sorusunun TÜRETİLDİĞİ yarısı (`StrandedAnalysesStrip`'in
  // diğer yarısı §20.1 `sources[]`'i kendi taşır) — §8 `documentUuid` burada, LİNKLENMİŞ her
  // belgenin kendi kimliği. Bir kaynağın `documentUuid`'i bu kümedeyse o kaynak zaten
  // bağlanmıştır; hiçbiri hatırlanmaz, her render'da listeden YENİDEN hesaplanır.
  const linkedDocumentUuids = useMemo(
    () => new Set(rows.map((document) => document.documentUuid)),
    [rows],
  );

  return (
    <div
      className="relative flex flex-col gap-6"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <DropOverlay active={dragActive} />

      <Breadcrumb items={[{ label: t('documents.title') }]} />
      <PageHeader
        title={t('documents.title')}
        helperText={t('documents.subtitle')}
        actions={
          /* S25 kenar çubuğu hiyerarşisi §13 — `flex-wrap`: Almanca uzun durumda (`Dokumentenanalyse`
             + `Dokument hochladen`) dar genişlikte TAŞMAZ, alt alta iner. */
          query.isSuccess ? (
            <ButtonSet className="flex-wrap justify-end">
              <AnalysisInboxButton />
              {/* Belge Yükle — bu sayfadaki TEK birincil eylem (tasarım review §4/AC30). Yalnız
                  firma bağı çözüldüğünde (liste başarıyla geldiğinde) gösterilir. */}
              <Button ref={uploadTrigger.ref} onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4" aria-hidden="true" />
                {t('documents.add')}
              </Button>
              {/* Gizli girdi yardımcı teknolojiden DE gizlenir (`personnel/PersonnelDocuments.tsx` ile
                  AYNI): tek başına adı olmadığından ekran okuyucuda "Choose File" diye duyuluyor
                  ve klavyede ikinci, adsız bir durak üretiyordu (QA D5). Klavye yolu HER ZAMAN
                  yukarıdaki gerçek `<button>`dır. */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={DOCUMENT_ACCEPT_ATTR}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []); // önce anlık görüntü
                  e.target.value = ''; // sonra sıfırla (aynı dosya tekrar seçilebilsin)
                  beginUpload(files);
                }}
              />
            </ButtonSet>
          ) : undefined
        }
      />

      {query.isSuccess ? (
        <div className="flex flex-col gap-3">
          <PendingAnalysesStrip />
          <StrandedAnalysesStrip
            companyId={companyId}
            scope={{ kind: 'contractor' }}
            linkedDocumentUuids={linkedDocumentUuids}
          />
        </div>
      ) : null}

      {query.isError ? (
        <ContractorStateNotice error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <Card className="carbon:p-0">
          <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
            {/* Uç noktada arama/sayfalama YOK (§13.6, dizi döner) → araç çubuğu yalnız özet sayı. */}
            <TableToolbar
              summary={query.data ? t('documents.list.count', { count: rows.length }) : undefined}
              density={density}
              onDensityChange={setDensity}
              densityLabel={t('common.compactRows')}
            />
            <Table caption={t('documents.title')} density={density}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('documents.col.type')}</TableHead>
                  <TableHead>{t('documents.col.file')}</TableHead>
                  <TableHead>{t('documents.col.validity')}</TableHead>
                  <TableHead>{t('documents.col.status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody aria-busy={query.isPending || undefined}>
                {query.isPending ? (
                  <TableSkeletonRows columns={5} />
                ) : rows.length === 0 ? (
                  <TableMessageRow colSpan={5}>
                    <EmptyState icon={FileText} title={t('documents.empty')} />
                  </TableMessageRow>
                ) : (
                  rows.map((document) => (
                    <TableRow key={document.uuid}>
                      {/* Kayıt etiketleri yöneticinin serbest metnidir (DE en uzunu): Carbon'da kırpılmaz,
                          satıra sarar (tasarım review §5). */}
                      <TableCell
                        truncate={false}
                        className="break-words font-medium text-fg carbon:h-auto carbon:py-2"
                      >
                        {typeLabel('company', document.type)}
                      </TableCell>
                      <TableCell truncate={false} className="text-muted">
                        <div className="flex min-w-0 flex-col">
                          <span className="text-fg [overflow-wrap:anywhere]">
                            {document.fileName ?? '—'}
                          </span>
                          {document.note ? (
                            <span className="text-xs text-muted">{document.note}</span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted tnum">
                        {validityRange(document, culture)}
                      </TableCell>
                      <TableCell>
                        {/* Expiring = gri + ikon (renk tek başına durum taşımaz). */}
                        <Tag
                          color={complianceColor(document.validityStatus)}
                          icon={
                            document.validityStatus === 'Expiring' ? (
                              <Hourglass aria-hidden="true" />
                            ) : undefined
                          }
                        >
                          {t(`compliance.${document.validityStatus.toLowerCase()}`)}
                        </Tag>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadMutation.mutate(document.documentUuid)}
                            loading={
                              downloadMutation.isPending &&
                              downloadMutation.variables === document.documentUuid
                            }
                            aria-label={t('documents.action.download')}
                          >
                            <Download className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              deleteTrigger.capture(e.currentTarget);
                              setToDelete(document);
                            }}
                            aria-label={t('documents.action.delete')}
                          >
                            <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <UploadModal
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) {
            // Partiler arasında `[]` ver (bkz. `UploadModal.tsx`'in `files` prop yorumu) —
            // eski `File[]`'i belleğe fazladan tutmayı önler, sonraki seçim zaten YENİ bir
            // referans yazar.
            setPickedFiles([]);
            // Odağı tetikleyiciye GERİ VER (QA D2) — app'teki TEK mekanizma üzerinden.
            uploadTrigger.restore();
          }
        }}
        files={pickedFiles}
        companyId={companyId}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setToDelete(null);
            deleteTrigger.restore();
          }
        }}
        title={t('documents.delete.title')}
        body={t('documents.delete.body', {
          type: toDelete ? typeLabel('company', toDelete.type) : '',
        })}
        confirmLabel={t('documents.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
